import { useMemo } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";

const QUOTES = [
  {
    body: "I hadn't seen my grandmother's favourite serial since I moved to Toronto ten years ago. My mum called me crying when I told her Nema TV had it live. Worth every dollar.",
    name: "Priya S.",
    city: "Toronto, Canada",
    region: "India",
    flag: "https://flagcdn.com/w80/in.png",
  },
  {
    body: "Cricket weekends with my dad, half a world apart. He watches from Karachi, I watch from Houston, and we text every over. That's what Desi TV means to us.",
    name: "Ahmed R.",
    city: "Houston, USA",
    region: "Pakistan",
    flag: "https://flagcdn.com/w80/pk.png",
  },
  {
    body: "News, music, dramas from Dhaka — all on my Fire TV in London. I finally feel like the flat has some of home in it.",
    name: "Nusrat K.",
    city: "London, UK",
    region: "Bangladesh",
    flag: "https://flagcdn.com/w80/bd.png",
  },
  {
    body: "I moved to Sydney for work but Kathmandu never really left me. Watching Nepali news over morning coffee makes the world feel smaller.",
    name: "Sabin T.",
    city: "Sydney, Australia",
    region: "Nepal",
    flag: "https://flagcdn.com/w80/np.png",
  },
  {
    body: "Ramadan nights just weren't the same in Berlin until Nema TV. Now the family gathers, the shows are on, and it truly feels like home.",
    name: "Layla H.",
    city: "Berlin, Germany",
    region: "Arab world",
    flag: "https://flagcdn.com/w80/sa.png",
  },
];

// Pick 3 quotes at random on every page load — returning visitors see fresh stories
const pickThree = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, 3);
};

const Testimonials = () => {
  const shown = useMemo(() => pickThree(QUOTES), []);
  return (
    <section
      id="stories"
      data-testid="testimonials-section"
      className="relative py-12 md:py-14"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-white/70 mb-4">
            Diaspora stories
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold leading-tight">
            A little piece of <span className="gradient-text">home,</span> every evening.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {shown.map((q, i) => (
            <motion.figure
              key={q.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`testimonial-card-${i}`}
              className="relative rounded-3xl p-6 md:p-7 bg-[#0F0A19] border border-white/10 hover:border-white/20 transition-colors"
            >
              <Quote className="absolute top-5 right-6 w-6 h-6 text-[#22D3EE]/40" aria-hidden="true" />
              <blockquote className="text-white/85 text-sm md:text-base leading-relaxed">
                &ldquo;{q.body}&rdquo;
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-white/5 pt-4">
                <img
                  src={q.flag}
                  alt={q.region}
                  loading="lazy"
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-[#0F0A19] shadow"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{q.name}</div>
                  <div className="text-xs text-white/50 truncate">
                    {q.city} · {q.region}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
